"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getTranslation, getLang } from '../config/config';
import { toast } from 'react-toastify';
import { useRouter, usePathname } from 'next/navigation';
import Cookies from 'js-cookie';

export interface TranslationType {
    language: string;
    translations: {
        page: string;
        values: {
            [field: string]: {
                [lang: string]: string;
            };
        };
    }[];
}

interface Language {
    uuid: string;
    name: string;
    direction: string;
    isActive: boolean;
    logo: string;
}

interface TranslationContextType {
    loading: boolean;
    translations: TranslationType[];
    supportedLanguages: Language[];
    language: string;
    selectedLanguage: string;
    handleLanguageChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
    setLanguage: (lang: string) => void;
    setSelectedLanguage: (lang: string) => void;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const LOCAL_STORAGE_KEY = 'cachedTranslations';

// Module-level flag to prevent multiple fetches
let hasFetchedTranslations = false;
let isFetchingTranslations = false;

export function TranslationProvider({ children }: { children: ReactNode }) {
    const [loading, setLoading] = useState(false);
    const [translations, setTranslations] = useState<TranslationType[]>([]);
    const [supportedLanguages, setSupportedLanguages] = useState<Language[]>([]);
    const router = useRouter();
    const pathname = usePathname();

    // Initialize language from cookie immediately to prevent flickering
    const getInitialLanguage = () => {
        if (typeof window !== 'undefined') {
            const saved = Cookies.get('lang');
            return saved || 'english';
        }
        return 'english';
    };

    const [language, setLanguage] = useState<string>(getInitialLanguage());
    const [selectedLanguage, setSelectedLanguage] = useState<string>(getInitialLanguage());

    // Fetch supported languages once
    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const languagesRes = await getLang();
                const languages: Language[] = languagesRes?.data?.languages || [];
                setSupportedLanguages(languages);
                
                // Save to cache
                const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
                if (cached) {
                    try {
                        const parsed = JSON.parse(cached);
                        localStorage.setItem(
                            LOCAL_STORAGE_KEY,
                            JSON.stringify({
                                translations: parsed.translations || [],
                                supportedLanguages: languages,
                            })
                        );
                    } catch (e) {
                        // Ignore parse errors
                    }
                }
            } catch (err) {
                console.error('Error fetching languages:', err);
            }
        };

        fetchLanguages();
    }, []);

    // Load translations from cache or API - runs only once globally
    useEffect(() => {
        const savedLanguage = Cookies.get('lang');
        if (savedLanguage && savedLanguage !== language) {
            setLanguage(savedLanguage);
            setSelectedLanguage(savedLanguage);
        } else if (!savedLanguage && language !== 'english') {
            setLanguage('english');
            setSelectedLanguage('english');
        }

        // Load from cache first
        const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                if (parsed.translations?.length > 0) {
                    setTranslations(parsed.translations);
                    if (Array.isArray(parsed.supportedLanguages) && parsed.supportedLanguages[0]?.logo) {
                        setSupportedLanguages(parsed.supportedLanguages);
                    }
                    // Cache exists, mark as fetched to prevent API call
                    hasFetchedTranslations = true;
                    return;
                }
            } catch (e) {
                console.error('Failed to parse cached translations', e);
            }
        }

        // No cache exists and haven't fetched yet - fetch from API (only once globally)
        if (!hasFetchedTranslations && !isFetchingTranslations) {
            hasFetchedTranslations = true;
            isFetchingTranslations = true;
            
            const fetchTranslations = async () => {
                setLoading(true);
                try {
                    const res = await getTranslation();
                    if (res?.status === 200) {
                        const fetchedTranslations = res?.data?.translations[0]?.languages || [];
                        setTranslations(fetchedTranslations);

                        // Save to cache
                        localStorage.setItem(
                            LOCAL_STORAGE_KEY,
                            JSON.stringify({
                                translations: fetchedTranslations,
                                supportedLanguages,
                            })
                        );
                    } else {
                        toast.error(res?.data?.message || 'Get Translation error');
                    }
                } catch (err) {
                    console.error('Get Translation error', err);
                    toast.error('Get Translation error');
                } finally {
                    setLoading(false);
                    isFetchingTranslations = false;
                }
            };

            fetchTranslations();
        }
    }, []); // Empty dependency array - runs only once on mount

    // Sync language from pathname changes
    useEffect(() => {
        const SUPPORTED_LANG_CODES = ['en', 'fr', 'ar'];
        const segments = pathname?.split('/').filter(Boolean);
        const langCode = segments[0];
        if (langCode && SUPPORTED_LANG_CODES.includes(langCode)) {
            const langName = langCode === 'ar' ? 'arabic' : langCode === 'fr' ? 'french' : 'english';
            document.cookie = `lang=${langName}; path=/; max-age=${60 * 60 * 24 * 30}`;
            setLanguage((prev) => (prev !== langName ? langName : prev));
            setSelectedLanguage((prev) => (prev !== langName ? langName : prev));
            return;
        }
        if (!langCode) {
            document.cookie = `lang=english; path=/; max-age=${60 * 60 * 24 * 30}`;
            setLanguage((prev) => (prev !== 'english' ? 'english' : prev));
            setSelectedLanguage((prev) => (prev !== 'english' ? 'english' : prev));
            return;
        }
        const match = document.cookie.match(/(^| )lang=([^;]+)/);
        const cookieLang = match ? decodeURIComponent(match[2]) : null;
        if (cookieLang) {
            setLanguage((prev) => (prev !== cookieLang ? cookieLang : prev));
            setSelectedLanguage((prev) => (prev !== cookieLang ? cookieLang : prev));
        }
    }, [pathname]);

    const handleLanguageChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
        const selectedLang = event.target.value as string;
        setSelectedLanguage(selectedLang);
        setLanguage(selectedLang);

        Cookies.set('lang', selectedLang, { path: '/', expires: 30 });
        const langCode = selectedLang === 'arabic' ? 'ar' : selectedLang === 'french' ? 'fr' : 'en';
        const currentPath = window.location.pathname;
        const pathWithoutLang = currentPath.replace(/^\/(en|ar|fr)/, '') || '/';

        if (langCode === 'en') {
            router.push(pathWithoutLang);
        } else {
            router.push(`/${langCode}${pathWithoutLang}`);
        }
    }, [router]);

    const value: TranslationContextType = {
        loading,
        translations,
        supportedLanguages,
        language,
        selectedLanguage,
        handleLanguageChange,
        setLanguage,
        setSelectedLanguage,
    };

    return (
        <TranslationContext.Provider value={value}>
            {children}
        </TranslationContext.Provider>
    );
}

export function useTranslations(): TranslationContextType {
    const context = useContext(TranslationContext);
    if (context === undefined) {
        throw new Error('useTranslations must be used within a TranslationProvider');
    }
    return context;
}

